// Copyright (C) 2024 Browser Streaming Service for Cloudflare

const { BROWSER_CONFIG } = require('./cloudflare-config');

class BrowserStreamingService {
    constructor() {
        this.useCloudflareWorkers = BROWSER_CONFIG.useCloudflareWorkers;
        this.enableBrowserStreaming = BROWSER_CONFIG.enableBrowserStreaming;
        this.mediaSource = null;
        this.sourceBuffer = null;
        this.videoElement = null;
    }

    // Initialize Media Source Extensions for browser streaming
    initializeMSE(videoElement) {
        if (!BROWSER_CONFIG.streaming.useMSE || !window.MediaSource) {
            console.log('Media Source Extensions not supported');
            return false;
        }

        this.videoElement = videoElement;
        this.mediaSource = new MediaSource();
        
        this.videoElement.src = URL.createObjectURL(this.mediaSource);
        
        this.mediaSource.addEventListener('sourceopen', () => {
            try {
                // Use common video codec
                this.sourceBuffer = this.mediaSource.addSourceBuffer('video/mp4; codecs="avc1.42E01E, mp4a.40.2"');
                this.sourceBuffer.mode = 'sequence';
                console.log('Media Source Extensions initialized successfully');
            } catch (error) {
                console.error('Error initializing MSE:', error);
            }
        });

        return true;
    }

    // Stream video data directly in browser using Cloudflare
    async streamVideo(streamUrl, videoElement) {
        try {
            // Initialize MSE if not already done
            if (!this.mediaSource && !this.initializeMSE(videoElement)) {
                // Fallback to direct streaming
                return this.fallbackStream(streamUrl, videoElement);
            }

            // Use Cloudflare Workers for torrent processing
            if (this.useCloudflareWorkers) {
                return await this.streamViaCloudflare(streamUrl);
            }

            // Direct streaming fallback
            return await this.fallbackStream(streamUrl, videoElement);
            
        } catch (error) {
            console.error('Browser streaming error:', error);
            return this.fallbackStream(streamUrl, videoElement);
        }
    }

    // Stream via Cloudflare Workers
    async streamViaCloudflare(streamUrl) {
        try {
            // Create a Cloudflare Worker-compatible stream URL
            const cloudflareStreamUrl = this.createCloudflareStreamUrl(streamUrl);
            
            const response = await fetch(cloudflareStreamUrl, {
                method: 'GET',
                headers: {
                    'Accept': 'video/*,application/*',
                    'Cache-Control': 'no-cache',
                },
                mode: 'cors',
                credentials: 'omit'
            });

            if (!response.ok) {
                throw new Error(`Cloudflare streaming failed: ${response.status}`);
            }

            // Get the video data
            const videoData = await response.arrayBuffer();
            
            // Append to MSE buffer if available
            if (this.sourceBuffer && !this.sourceBuffer.updating) {
                this.sourceBuffer.appendBuffer(videoData);
            }

            return {
                success: true,
                url: cloudflareStreamUrl,
                data: videoData
            };

        } catch (error) {
            console.error('Cloudflare streaming error:', error);
            throw error;
        }
    }

    // Create Cloudflare-compatible stream URL
    createCloudflareStreamUrl(originalUrl) {
        // Handle different types of stream URLs
        
        // If it's already a Cloudflare optimizer URL, use it directly
        const cloudflareOptimizers = [
            'torrentio.strem.fun',
            'comet.elfhosted.com',
            'mediafusion.elfhosted.com',
            'annatar.elfhosted.com',
            'jackettio.elfhosted.com',
            'orionoid.com',
            'stremthru.elfhosted.com'
        ];
        
        const urlObj = new URL(originalUrl);
        if (cloudflareOptimizers.some(domain => urlObj.hostname.includes(domain))) {
            return originalUrl;
        }
        
        // Convert torrent/magnet links to Cloudflare Worker URLs
        if (originalUrl.includes('magnet:') || originalUrl.includes('.torrent')) {
            // Use Comet as the primary Cloudflare Worker
            return `https://comet.elfhosted.com/stream?url=${encodeURIComponent(originalUrl)}`;
        }
        
        // For direct video URLs, check if they need proxying
        if (originalUrl.match(/\.(mp4|webm|ogg|mkv|avi|mov)$/i)) {
            // Check if it's from a CORS-enabled source
            const corsEnabledDomains = [
                'videodelivery.net',
                'cloudflare.com',
                'workers.dev'
            ];
            
            if (corsEnabledDomains.some(domain => urlObj.hostname.includes(domain))) {
                return originalUrl;
            }
            
            // Use Cloudflare Stream for other video URLs
            return `https://videodelivery.net/${encodeURIComponent(originalUrl)}`;
        }

        // Return original URL if no transformation needed
        return originalUrl;
    }

    // Fallback streaming method
    fallbackStream(streamUrl, videoElement) {
        try {
            // Direct video source assignment
            videoElement.src = streamUrl;
            videoElement.crossOrigin = 'anonymous';
            
            return {
                success: true,
                method: 'direct',
                url: streamUrl
            };
        } catch (error) {
            console.error('Fallback streaming error:', error);
            return {
                success: false,
                error: error.message
            };
        }
    }

    // Check browser compatibility
    checkBrowserCompatibility() {
        const features = {
            mediaSource: !!window.MediaSource,
            webRTC: !!window.RTCPeerConnection,
            fetch: !!window.fetch,
            cors: 'withCredentials' in new XMLHttpRequest(),
            serviceWorker: 'serviceWorker' in navigator
        };

        console.log('Browser compatibility check:', features);
        return features;
    }

    // Clean up resources
    cleanup() {
        if (this.sourceBuffer) {
            try {
                if (!this.sourceBuffer.updating) {
                    this.sourceBuffer.remove(0, this.sourceBuffer.buffered.end(0));
                }
            } catch (error) {
                console.warn('Error cleaning up source buffer:', error);
            }
        }

        if (this.mediaSource) {
            try {
                if (this.mediaSource.readyState === 'open') {
                    this.mediaSource.endOfStream();
                }
            } catch (error) {
                console.warn('Error ending media source stream:', error);
            }
        }

        if (this.videoElement && this.videoElement.src) {
            URL.revokeObjectURL(this.videoElement.src);
        }

        this.mediaSource = null;
        this.sourceBuffer = null;
        this.videoElement = null;
    }
}

// Export singleton instance
module.exports = new BrowserStreamingService();